import axios from 'axios';
import { apiUrl, IS_DEMO } from '../../apiBase';
import type { Material } from './materialsRun';
import type { PictureSource } from './pictureRef';
import { IMPORT_MODEL, importOf, type Job3D, type PieceItem, type ProductMark, type Status3D } from './threeDApi';
import { isHardwareOrTool, type PartItem } from './partsRun';
import type { CategoryDef } from './catalogCategories';
import { materialText, type MaterialPair } from './variantLabel';
import type { PartCrop } from './productPartCrops';
import type { DimensionLine, DimensionLineSpec } from './dimensionLines';

// THE WRITTEN MODEL's half of /api/notice/3d — the models phase's engine. An OpenAI model
// is shown photographs and writes the bpy that builds it; the server's own Blender
// runs that script and exports what it made. The run it produces is a Job3D like any
// other, so the viewer, the history and the delete button are the ones already there.
export { meshSrc, deleteJob, falError } from './threeDApi';

// What a run of it is called, and so what its rows say. The server prefixes the model id
// the same way — one string, spelled here for the page that wants to know which engine a
// run came from without asking the server what OpenAI models exist.
export const OPENAI_PREFIX = 'openai/';

export const isWrittenRun = (job: Job3D) => (job.model || '').startsWith(OPENAI_PREFIX);

export type OpenAIStatus = Status3D['openai'];

// THE MODEL THAT WRITES THE MESH, for every generator: the first the server offers, and none
// on a server without an OpenAI key — which cannot make a model, and whose button says so by
// being dead. Not chosen on the page: there is no dropdown over it.
export const writtenEngineOf = (probe: Status3D | null): string | null =>
  (probe?.openai?.configured ? probe.openai.models[0]?.id ?? null : null);

// WHAT MADE THIS RUN, for the viewer's heading — which model wrote the one on screen.
//
// From the PROBE for the written models, not from a list spelled here: the labels are the
// server's answer about its own key, and a second copy would drift from it
// the day a model is added. A run made by a model since retired is not in the probe at
// all, and its own id names it better than nothing does — an old run reads "gpt-5.4".
//
// null for everything else on purpose. A CAD upload and a lathe turn are runs in this
// strip too, and neither was generated by a model; naming one would be inventing an
// answer, and the heading is correct without a parenthesis.
export function engineLabelOf(
  job: Job3D | null | undefined, probe: Status3D | null,
): string | null {
  const model = job?.model || '';
  // IN THE DEMO, NO MODEL IS NAMED: the demo shows what the app does, not which AI does it.
  if (!model || IS_DEMO) return null;
  if (model.startsWith(OPENAI_PREFIX)) return writtenModelLabel(probe, model.slice(OPENAI_PREFIX.length));
  // AN IMPORTED MODEL, by the file it came from.
  if (model === IMPORT_MODEL) return importOf(job)?.filename || null;
  return null;
}

// WHICH ROW A RUN BY THIS ENGINE WOULD LAND IN — the `model` a run made now would carry.
//
// The strip groups by that column, so a tile drawn for a run in flight has to be filed the
// same way the finished one will be, or it appears in one row and lands in another: the prefix
// and the model id.
export const engineModelKey = (engine: string): string => OPENAI_PREFIX + engine;

// EVERY MODEL A WRITTEN RUN COULD BE FILED UNDER, for the histories. From the probe rather than from a constant: what OpenAI
// offers is the server's answer, and a list spelled here would go stale against it.
export const writtenModelKeys = (probe: Status3D | null): string[] =>
  writtenModelsOf(probe).map((m) => OPENAI_PREFIX + m.id);

// A written model's name — "GPT-6 Astra" — or its id where the probe does not know it.
export const writtenModelLabel = (probe: Status3D | null, model: string): string =>
  writtenModelsOf(probe).find((m) => m.id === model)?.label || model;

// Every written model a run could carry: the ones on offer, and the ones retired from the
// dropdown whose runs are still in somebody's history (see RETIRED_MODELS on the server).
const writtenModelsOf = (probe: Status3D | null): { id: string; label: string }[] =>
  [...(probe?.openai?.models || []), ...(probe?.openai?.retired || [])];

// HOW HARD THE CHOSEN MODEL THINKS, and what it may be set to — the server's list per model.
export const effortChoices = (probe: Status3D | null, model: string): string[] =>
  probe?.openai?.models.find((m) => m.id === model)?.efforts ?? [];
export const defaultEffort = (probe: Status3D | null, model: string): string =>
  probe?.openai?.models.find((m) => m.id === model)?.defaultEffort ?? '';

// THE EFFORT A RUN RAN AT, off the run — what OpenAI reported, recorded per pass.
export const effortOf = (job: Job3D | null | undefined): string | null =>
  (typeof job?.params?.effort === 'string' ? job.params.effort : null);

// A model's name with the effort it ran at — "GPT-6 Astra - high" — for the heading over the
// mesh. Not in engineLabelOf, whose label also names a ROW of runs in the strip, and a row
// holds runs made at different efforts.
export const withEffort = (label: string | null, job: Job3D | null | undefined): string | null => {
  const effort = effortOf(job);
  // Nor how hard it thought, in the demo.
  if (IS_DEMO) return null;
  return label && effort ? `${label} - ${effort}` : label;
};

// HOW MANY PASSES THE PANEL MAY OFFER, and which of them it starts on.
//
// A pass is a call and a build: the first writes a script from the photograph alone, and
// each one after is shown renders of what the last one built and asked to correct it. Two
// is where nearly all of the improvement is; the ones after are for a shape the second look
// did not settle.
//
// From the probe, with a fallback for a server that predates the field — offering a choice
// the route would clamp is offering a choice that is not there.
export const passChoices = (probe: Status3D | null): number[] =>
  (probe?.openai?.passes?.length ? probe.openai.passes : [1, 2, 3, 4, 5]);
export const defaultPasses = (probe: Status3D | null): number =>
  probe?.openai?.defaultPasses ?? 2;

// The written engine's half of the one status probe, kept separable from the fetch:
// /status answers for every engine at once.
export const openaiStatusOf = (s: Status3D): OpenAIStatus => s.openai;

// THE SIZE TO BUILD IT AT, in millimetres: the box's three axes and, where the drawing printed
// both, the bottom and top of the width and the depth. What the written engine is told — see
// size_block on the server, which turns it into metres and a sentence. Undefined when nothing
// was read, which sends nothing rather than a size of nothing.
export type WrittenSize = {
  x: number | null; y: number | null; z: number | null;
  xBottom: number | null; xTop: number | null; yBottom: number | null; yTop: number | null;
} & Partial<Record<(typeof HARDWARE_KEYS)[number], number | string | null>>;

// AND THE WAY A PIECE OF HARDWARE IS MEASURED, which is not three axes. A barrel nut has a body
// diameter and a length; a screw has a thread, a total length and two diameters; a hex key has a
// drive size and two legs. The parts table has held these since it had categories (formatUnits,
// whose keys these are) and none of them could reach a prompt that carried x, y and z — so the
// one call that most needs a number, a part nobody photographs, was the one asked to guess it.
//
// SENT WHATEVER THE PART IS. Which of these describe it is the category's answer and it has
// already been given: `row.size` is passed through sizeForCategory, so a part that is a bounding
// box arrives with these empty and a screw arrives with the axes empty. Filtering again here
// would be a second opinion on a question that is settled.
const HARDWARE_KEYS = [
  'lengthMm', 'bodyDiaMm', 'headDiaMm', 'fitsHoleMm', 'driveSizeMm', 'shortLegMm',
  'thread', 'drive', 'forScrew', 'spec',
] as const;

type SizeLike = {
  x?: number | null; y?: number | null; z?: number | null;
  xBottom?: number | null; xTop?: number | null; yBottom?: number | null; yTop?: number | null;
} & Partial<Record<(typeof HARDWARE_KEYS)[number], number | string | null | undefined>>
  | null | undefined;

export function writtenSize(size: SizeLike): WrittenSize | undefined {
  if (!size) return undefined;
  const out: WrittenSize = {
    x: size.x ?? null, y: size.y ?? null, z: size.z ?? null,
    xBottom: size.xBottom ?? null, xTop: size.xTop ?? null,
    yBottom: size.yBottom ?? null, yTop: size.yTop ?? null,
  };
  for (const key of HARDWARE_KEYS) out[key] = size[key] ?? null;
  return Object.values(out).some((v) => v != null) ? out : undefined;
}

// AND THE SIZE A RUN WAS TOLD, off the run — for a badge about a call already made, which must
// show what that call said and not what the next press would.
export const writtenSizeOf = (job: { params?: Record<string, unknown> | null } | null | undefined) =>
  writtenSize(job?.params?.size as SizeLike);

// THE PARTS A PRODUCT IS MADE OF, as the written engine is told them — see parts_block on the
// server: a name, a count, and the part's own drawn box. COMPONENT PARTS ONLY: hardware and
// tools stay out (see isHardwareOrTool) — a screw is not what the model is building — and so
// does anything the box does not hold. Undefined where nothing is left, which sends nothing.
// `material` is Phase 2's, as its words read ("Tissu, beige"); null where the part was given none.
export type WrittenPart = { name: string; qty: number | null; size?: WrittenSize; material?: string | null };

// THE HARDWARE A PIECE OF HARDWARE WORKS WITH — one row of Phase 4's hardware table, as the
// part's build is told it: the other hardware's names, the component parts it goes through, and
// how they engage. What orients the build — see openai_blender.interactions_block.
export type WrittenInteraction = {
  with: string[]; through: string[]; how: string;
  // BUILT TOGETHER, in one scene — a hardware set, where `with` is every part of it, and
  // `drawn` names the part each of its pictures is the drawing of.
  together?: boolean;
  drawn?: string[];
  // A COVER: the part being built sits on the others' hole, or on `through` alone — or, of a set,
  // which of its parts do. Built contact face down (see openai_blender._COVER_RULE).
  cover?: boolean;
  covers?: string[];
  // A TOOL BUILT ON ITS OWN: the part being built DRIVES `with`, each named with its printed size
  // and drive (see toolInteractions). Its tip is made for that drive and it is stood tip down, on
  // the axis the fastener goes in along — the server's rule, from this flag.
  tool?: boolean;
  // A TOOL BUILT WITH WHAT IT DRIVES: which part of a set is the tool (see toolSet). The tip and the
  // recesses are cut against each other in one scene — openai_blender._driver_block.
  driver?: string;
  // AND WHICH OF A SET'S PARTS ARE IN THE SCENE ALREADY, made with their tools — named here so the
  // prompt says so; the server adds what their kept models measure (see notice_3d._reused).
  reused?: { name: string }[];
};

// The ones a run was sent, off its own row — what the prompt panel of a past pass shows.
export const interactionsOfRun = (job: Job3D | null | undefined): WrittenInteraction[] =>
  (Array.isArray(job?.params?.interactions) ? job.params.interactions as WrittenInteraction[] : []);

// `materials` is Phase 2's pick for each part of the submodel being modelled, by the part's index
// in `items` — the parts table's own keying. A part with no pick goes as having none, which the
// prompt says in so many words.
// THE NAME A PART TRAVELS UNDER, and the name every run records it by: English where Phase 2
// read one, because the model is written to in English and a part called "Dossier et piètement
// arrière" in the prompt comes back as a mesh nobody can match to the list.
export const writtenPartName = (p: PartItem) => (p.nameEn || p.name || p.ref || '').trim();

// WHICH ROWS OF A PARTS LIST A WRITTEN BUILD IS TOLD ABOUT, by index: the components that are
// supplied and have a name to be called by.
//
// By index and on its own, because the answer is now wanted twice. The prompt wants the PARTS
// (below); the press that keeps a whole product's components wants the catalog ROW behind each
// one, to file the mesh it cuts out under — and a second copy of this filter is a second
// answer to "what is this product made of", which is exactly the disagreement that would put
// a screw's mesh on a chair leg's row.
export function writtenPartRows(items: PartItem[], categories?: CategoryDef[]): number[] {
  return items
    .map((p, at) => ({ p, at }))
    .filter(({ p }) => !isHardwareOrTool(p, categories) && p.supplied !== false)
    .filter(({ p }) => !!writtenPartName(p))
    .map(({ at }) => at);
}

export function writtenParts(
  items: PartItem[], materials?: Record<number, MaterialPair | null>,
  // THE ONTOLOGY, WHERE THE CALLER WAITED FOR IT. Which parts are the furniture and which are
  // the screws holding it together is the ontology's answer, and it arrives over the network:
  // read off the module cache before that lands, every screw is a component and goes into the
  // prompt as a piece of wood to model and a material to judge. A caller building a prompt
  // passes what ensureCategories gave it.
  categories?: CategoryDef[],
): WrittenPart[] | undefined {
  const out = writtenPartRows(items, categories).map((at) => {
    const pair = materials?.[at];
    return {
      name: writtenPartName(items[at]),
      qty: items[at].qty,
      size: writtenSize(items[at].size),
      material: pair ? materialText(pair) : null,
    };
  });
  return out.length ? out : undefined;
}

// And the parts a run was told, off the run — for a badge about a call already made.
export const writtenPartsOf = (job: { params?: Record<string, unknown> | null } | null | undefined) =>
  (Array.isArray(job?.params?.parts) ? job.params.parts as WrittenPart[] : undefined);

// THE PART A MESH IS, by its name — which the model is told to make EXACTLY the part's name as
// listed, with " (Left)" or " (Right)" on the end where the part comes as a pair (see
// parts_block). So nothing is guessed here: a mesh named anything else is no part, and its
// "wanted" row reads "?". The part's drawn size, or null.
export function partSizeOfMesh(
  mesh: string, parts: WrittenPart[] | undefined,
): { x: number | null; y: number | null; z: number | null } | null {
  const name = mesh.trim().replace(/ \((Left|Right)\)$/, '');
  const size = (parts ?? []).find((part) => part.name.trim() === name)?.size;
  // NONE WHERE THE PART HAS NO BOX — a piece of hardware is said as a length and a thread — so
  // its mesh says nothing wanted rather than "wanted: ?" on all three.
  if (!size || (size.x == null && size.y == null && size.z == null)) return null;
  return { x: size.x, y: size.y, z: size.z };
}

// THE DIMENSIONS A WRITTEN SCRIPT OPENS WITH, as the server read them off it when the run was
// saved — [] for a script that has none. And a rebuild at new values: the same script, run
// again with those numbers, saved as the run's next pass. See rebuild_written.
// `group` is the mesh it sizes, as the script named it; '' or absent on a script that named none.
export type WrittenDimension = { name: string; value: number; note: string; group?: string };

export const dimensionsOf = (job: { params?: Record<string, unknown> | null } | null | undefined) =>
  (Array.isArray(job?.params?.dimensions) ? job.params.dimensions as WrittenDimension[] : []);

// "SEAT_HEIGHT" as a label: "Seat height".
export const dimensionLabel = (d: { name: string }) => {
  const words = d.name.toLowerCase().split('_').filter(Boolean).join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// WHERE EACH DIMENSION IS MEASURED, as the script's measure() calls put it — segments in
// millimetres, in the file's frame; a call that was given no segment is left out, the checks
// having said so. [] for a model built before it was asked, which addWrittenLines can fill.
export const dimensionLinesOf = (job: Job3D | null | undefined): DimensionLine[] =>
  ((job?.params as { lines?: (DimensionLine | { name: string; error: string })[] })?.lines ?? [])
    .filter((l): l is DimensionLine => 'start' in l);

// The lines of the model on screen, with what the viewer says beside each: its group, its
// number, its label. Null where it has none to draw.
export const dimensionLineSpecOf = (job: Job3D | null | undefined) => {
  const lines = dimensionLinesOf(job);
  if (!lines.length) return null;
  const about: DimensionLineSpec['about'] = {};
  for (const d of dimensionsOf(job)) {
    about[d.name] = { group: shownGroup(d.group || WHOLE_OBJECT), value: d.value, label: dimensionLabel(d) };
  }
  return { lines, about };
};

// The dimension groups that name no mesh, and how the others join theirs — written_checks's twins.
// "Whole object" is the object's own overall extents; "Seat & Side rail (Left)" is two meshes.
export const WHOLE_OBJECT = 'Whole object';
export const OTHER_GROUP = 'Other';
export const GROUP_JOIN = ' & ';

// A DIMENSION'S GROUP AS IT IS SHOWN: a group of more than three meshes is "Other".
export const shownGroup = (group: string) =>
  (group && group !== OTHER_GROUP && group.split(GROUP_JOIN).length > 3 ? OTHER_GROUP : group);

// THE ORDER GROUPS ARE READ IN — the whole object (and a script's untitled group), one mesh each,
// two, three, "Other" — each band alphabetical. The panel's order and the viewer menu's.
export const compareGroups = (a: string, b: string) => {
  const size = (g: string) => (!g || g === WHOLE_OBJECT ? 0 : g === OTHER_GROUP ? 4 : g.split(GROUP_JOIN).length);
  return size(a) - size(b) || a.localeCompare(b);
};

// WHERE AN OLD MODEL'S DIMENSIONS ARE MEASURED, asked afterwards: a small call writes the
// measure() lines, and the model with them is saved as a new version of its pass — refused if a
// mesh moved. See add_written_lines.
export async function addWrittenLines(jobId: number): Promise<Job3D> {
  const { data } = await axios.post<{ job: Job3D }>(`/api/notice/3d/written/${jobId}/lines`, {});
  return data.job;
}

export async function rebuildWritten(jobId: number, values: Record<string, number>): Promise<Job3D> {
  const { data } = await axios.post<{ job: Job3D }>(
    `/api/notice/3d/written/${jobId}/dimensions`, { values },
  );
  return data.job;
}

// WHAT THE CALL WILL SAY, asked of the server rather than assembled here. The system
// prompt lives in backend/openai_blender.py and changing it is a deploy; a copy in this
// file would be a second prompt that looks like the first one right up until it does not,
// and a panel nobody can trust is worse than no panel. The photograph is added by the
// caller, which already knows which one it is about to send.
export async function writtenPrompt(
  subject?: string, model?: string, round?: 2,
  // THE PICTURES IT IS ABOUT TO BE SHOWN, in order — because the words change with them:
  // one photograph is one sentence, several is a numbered list naming the angle of each.
  views?: string[],
  // And the size it would be told, where there is one.
  size?: WrittenSize,
  // And the parts it would be told it is made of.
  parts?: WrittenPart[],
  // AND, FOR THE SECOND ROUND, what the build it corrects measured — the numbers it was given.
  built?: WrittenBuilt,
  // AND WHAT IT WOULD BE TOLD IT IS MADE OF — Phase 5's first subphase's answer. The build no
  // longer judges its own materials (see MATERIALS_SYSTEM), so a preview without them would be
  // the question as it was before they were split out.
  materials?: Material[],
  // AND THE CLOSE-UPS IT WOULD BE SHOWN after the photographs, where there are any — their
  // captions, which is all the words need. See productPartCrops.
  crops?: { part: string; view: string }[],
  // THE NEXT PRESS'S CORRECTION, for round 2: nothing is built for it yet, so what the pass
  // before will have measured and written stands as placeholders (openai_blender.PENDING_*).
  upcoming?: boolean,
  // AND WHETHER THE PICTURE IS A DRAWING. The prompt's opening sentence turns on it — "a schema
  // of a hardware part" against "a photograph of a piece of furniture" — so a preview that did
  // not say would be showing the other call's words. See _OPENING on the server.
  schema?: boolean,
  // AND THE HARDWARE IT WORKS WITH, for a piece of hardware.
  interactions?: WrittenInteraction[],
): Promise<{ prompt: string; model: string }> {
  const { data } = await axios.get<{ prompt: string; model: string }>(
    '/api/notice/3d/written/prompt',
    {
      params: {
        ...(subject ? { subject } : {}),
        ...(model ? { model } : {}),
        ...(round ? { round } : {}),
        ...(round && upcoming ? { upcoming: 1 } : {}),
        ...(views?.length ? { view: views.map((v) => v || '') } : {}),
        ...(size ? { size: JSON.stringify(size) } : {}),
        ...(parts?.length ? { parts: JSON.stringify(parts) } : {}),
        ...(built ? { built: JSON.stringify(built) } : {}),
        ...(materials?.length ? { materials: JSON.stringify(materials) } : {}),
        ...(crops?.length
          ? { crops: JSON.stringify(crops.map(({ part, view }) => ({ part, view }))) }
          : {}),
        ...(schema ? { schema: 1 } : {}),
        ...(interactions?.length ? { interactions: JSON.stringify(interactions) } : {}),
      },
      // Repeated keys rather than one comma-joined value: `view=front&view=` is a list of
      // two on the server, and an empty one in the middle keeps the numbering honest.
      paramsSerializer: { indexes: null },
    },
  );
  return data;
}

// THE RENDERS THE SECOND ROUND WAS SHOWN, which exist only where there was one. Named by
// the run and the direction, so the page needs no list of files — `draftShots` on the run
// says which were kept, and this turns each into an address.
export const draftShotSrc = (jobId: number, name: string) =>
  apiUrl(`/api/notice/3d/model/${jobId}/draft/${name}`);

// AND WHAT CAME BACK, off the run itself. Every one of these is stored in the run's params
// because the run is the only place they exist together: api_usage keeps the token counts
// and not the words, and the mesh keeps neither.
//
// A FAILED run answers this too, and is the one most worth reading — the script that did
// not run is on the row beside the traceback that says why.
export type WrittenCall = {
  reasoning: string;
  script: string;
  took: number | null;
  // THE MIDDLE OF THE RUN, on the runs that had one. A written build gets one correction:
  // its first script is kept, and so are the renders that were sent back to be corrected
  // from — the mesh they show does not exist any more, round two having overwritten it.
  //
  // Null on a single-round run, which is every run made before the second round existed
  // and every run whose correction was skipped. `revisionSkipped` says which of those.
  draft: {
    jobId: number; script: string; reasoning: string; shots: string[];
    // What that build measured and what its checks found, as the correction was told them.
    built: WrittenBuilt | null;
  } | null;
  skipped: string;
  // WHAT A CORRECTION SAID DIFFERS from the photographs, one line per difference, before it
  // wrote its script — [] for a first pass and for a correction made before it was asked.
  differences: string[];
};

// WHICH PICTURES A RUN WAS ACTUALLY SHOWN, in the order it was shown them.
//
// Off the run, because that is where it is true: the panel's own pick is what the NEXT
// press would send, and a badge in the viewer is about a call already made. `inputs` is
// written on every run since each picture carries the pixels it was sent at; a run from
// before that, made from a single photograph, has it only in the row's image column.
export const writtenInputsOf = (job: Job3D): { imageId: number; view?: string }[] => {
  const params = job.params as { inputs?: { imageId?: number; view?: string }[] };
  const said = (params.inputs || []).filter((one) => typeof one.imageId === 'number');
  if (said.length) return said as { imageId: number; view?: string }[];
  return job.imageId == null ? [] : [{ imageId: job.imageId }];
};

export function writtenCallOf(job: Job3D | null | undefined): WrittenCall | null {
  if (!job || !isWrittenRun(job)) return null;
  const params = job.params as {
    script?: string; reasoning?: string; took?: number; draftShots?: string[];
    revisionSkipped?: string; draftBuilt?: WrittenBuilt;
    firstDraft?: { script?: string; reasoning?: string };
    differences?: string[];
  };
  if (!params.script) return null;
  const first = params.firstDraft;
  return {
    reasoning: params.reasoning || '',
    script: params.script,
    took: params.took ?? job.elapsedSeconds,
    draft: first?.script ? {
      jobId: job.id,
      // The renders are filed under the run that was SHOWN them, which is this one — see
      // draft_shot_path, and generate_written, which moves them there as each pass is saved.
      script: first.script,
      reasoning: first.reasoning || '',
      shots: params.draftShots || [],
      built: params.draftBuilt ?? null,
    } : null,
    skipped: params.revisionSkipped || '',
    differences: params.differences || [],
  };
}

// WHAT A BUILD MEASURES AND WHAT WAS WRONG WITH IT — every mesh's box in millimetres, its volume and
// its open edges, as Blender read them, and the sentences written_checks made of them.
export type WrittenBuilt = {
  // `materials` is what the mesh wears, by name — absent on a build made before it was read.
  measures: {
    name: string; min: number[]; max: number[]; volume: number; open: number; materials?: string[];
  }[];
  checks: string[];
};

// WHAT THE BUILD ACTUALLY MEASURES, in millimetres: the whole object's box, folded out of every
// mesh's own. Blender measured it the moment the script had run (see written_checks, which holds
// it against the drawing), and it is kept on the run — so this is not an estimate of the size, it
// is the size, of the very file the viewer is showing.
//
// A written mesh is built in metres from millimetres the model chose, and Blender then read the
// result back. See mmScale, where it is the last resort and the printed size still comes first.
//
// Null for a run with no measures — every run that was not written, and every written run made
// before they were taken.
export function builtBoxOf(
  job: { params?: Record<string, unknown> | null } | null | undefined,
): { x: number; y: number; z: number } | null {
  const b = builtBoundsOf(job);
  if (!b) return null;
  const box = { x: b.max[0] - b.min[0], y: b.max[1] - b.min[1], z: b.max[2] - b.min[2] };
  return box.x > 0 || box.y > 0 || box.z > 0 ? box : null;
}

// AND WHERE THAT BOX STANDS, in the frame the build was made in: its two corners, in millimetres.
// A piece cut from a set keeps its own meshes' measures (see _keep_pieces), still written in the
// SET's frame — so two pieces of one set say, between them, where each stands against the other.
export function builtBoundsOf(
  job: { params?: Record<string, unknown> | null } | null | undefined,
): { min: [number, number, number]; max: [number, number, number] } | null {
  const measures = Array.isArray(job?.params?.measures)
    ? job.params.measures as { min?: number[]; max?: number[] }[]
    : [];
  const boxes = measures.filter((m) => m.min?.length === 3 && m.max?.length === 3);
  if (!boxes.length) return null;
  const lo = (i: number) => Math.min(...boxes.map((m) => m.min![i]));
  const hi = (i: number) => Math.max(...boxes.map((m) => m.max![i]));
  return { min: [lo(0), lo(1), lo(2)], max: [hi(0), hi(1), hi(2)] };
}

// THE CHECKS ON A RUN, or null for a run made before there were any — which says nothing, where []
// says everything passed.
export const checksOf = (job: Job3D | null | undefined): string[] | null =>
  (Array.isArray(job?.params?.checks) ? job.params.checks as string[] : null);

// WHERE TWO OF ITS PARTS FILL THE SAME SPACE, as Blender measured the build (see
// written_checks.overlaps_of): each pair, the box they share in millimetres and its smallest side,
// `depth`. Null for a version built before the check, which is not the same as none.
// And WHERE each is, `min` to `max` in the product's frame — which the Fix collisions call is told —
// and `mesh`, the number of its shared solid on disk, laid over the model when its row is pressed.
//
// UNCHECKED, AND SO CHECKED ON SIGHT (see ProductOverlaps): a version with no overlaps kept, one
// kept before their places were, and one with overlaps whose solids and see-through renders were
// never drawn — a build measures its overlaps and draws neither (see check_overlaps).
export type Overlap = {
  a: string; b: string; mm: [number, number, number]; volume: number; depth: number;
  min: [number, number, number]; max: [number, number, number]; mesh?: number;
};
// And one drawn by an earlier check than OVERLAP_CHECK — written_checks' number, kept equal: the
// check it names counts shared space on a grid, where the ones before misread contact as overlap.
const OVERLAP_CHECK = 3;
export const overlapsOf = (job: Job3D | null | undefined): Overlap[] | null => {
  const params = job?.params as { overlaps?: unknown; overlapCheck?: number } | undefined;
  const said = params?.overlaps;
  if (!Array.isArray(said)) return null;
  if (!(said as Partial<Overlap>[]).every((o) => Array.isArray(o.min))) return null;
  return said.length && (params?.overlapCheck ?? 0) < OVERLAP_CHECK ? null : said as Overlap[];
};

// ONE OVERLAP'S SHARED SOLID, and a see-through render of the version with its overlaps red.
export const overlapMeshSrc = (jobId: number, n: number) => apiUrl(`/api/notice/3d/model/${jobId}/overlap/${n}`);
export const collideShotSrc = (jobId: number, name: string) =>
  apiUrl(`/api/notice/3d/model/${jobId}/collide/${name}`);
export const collideShotsOf = (job: Job3D): string[] =>
  ((job.params as { collideShots?: string[] }).collideShots ?? []);

// THE CALL THAT PUT A VERSION'S COLLISIONS RIGHT, on the version it made: what it thought and what
// it wrote. Null on every other version.
export type CollisionsFix = { model: string; reasoning: string; said: string; call: number; fixed: number };
export const collisionsFixOf = (job: Job3D | null | undefined): CollisionsFix | null =>
  ((job?.params as { rebuilt?: { collisions?: CollisionsFix } })?.rebuilt?.collisions ?? null);

// FIX COLLISIONS, AS ONE VERSION: a call rewrites the version's script so its overlapping parts only
// touch (skipped where every collision is already shallow), what is left and shallow is cut away in
// Blender, and only the result is saved and checked. A written run: asked after, and stopped, by its
// token. The version, or null where it was stopped. See fix_written_collisions.
export async function fixCollisions(
  jobId: number,
  stop: { token: string; signal: AbortSignal },
  onProgress?: (progress: RunProgress) => void,
): Promise<{ job: Job3D | null; note?: string }> {
  await axios.post(`/api/notice/3d/written/${jobId}/collisions`, { token: stop.token }, { signal: stop.signal });
  return waitWrittenRun(stop.token, stop.signal, onProgress);
}

// And what that call would send, for its badge.
export async function collisionsPrompt(jobId: number): Promise<{ prompt: string; model?: string }> {
  const { data } = await axios.get(`/api/notice/3d/written/${jobId}/collisions/prompt`);
  return data;
}

// AND MEASURED ON ITS OWN FILE, for a version built before the build measured it: kept on the
// version, which comes back.
export async function checkOverlaps(jobId: number): Promise<Job3D> {
  const { data } = await axios.post(`/api/notice/3d/jobs/${jobId}/overlaps`);
  return data.job as Job3D;
}

// WHAT A WRITTEN RUN IS MADE OF, as its first pass stated it — one MATERIAL line each: the colour
// as sRGB, how rough and how metallic, and whether Phase 2 gave it to a part or the model named it
// for a part given none. Every pass of the run is built with the same. [] for a run made before
// materials were stated.
// WHAT A RUN WAS BUILT WITH — the materials on its own row, which is not the same list as
// the product's current answer: the materials can have been read again since, and a pass's
// badge shows the question that was actually put to it.
//
// Recorded on every pass (see generate_written) and read back by the rebuild, which regrains
// the mesh with the numbers it was grained with the first time.
export const builtWith = (job: Job3D | null | undefined): Material[] =>
  (Array.isArray(job?.params?.materials) ? job.params.materials as Material[] : []);

// WHAT A RUN MADE BEFORE MATERIALS WERE STATED MEASURED ITS OWN MATERIAL TO BE — the colour of the
// patch tiled on its mesh,
// and how rough and metallic the model judged it from the photograph.
//
// PER RUN AND NOT PER MATERIAL, deliberately. The material's colour is pooled across every
// part made of that wood and is somebody's choice; this is one crop of one picture, and
// letting it redefine the finish everywhere would be one leg deciding for the chair. It
// only ever paints the run it came from.
export type RunSurface = {
  rgb?: [number, number, number];
  roughness?: number;
  metallic?: number;
};

export function runSurfaceOf(job: Job3D): RunSurface {
  const params = job.params as {
    texture?: { rgb?: number[] };
    surface?: { roughness?: number; metallic?: number };
  };
  const rgb = params.texture?.rgb;
  return {
    ...(rgb && rgb.length === 3 ? { rgb: [rgb[0], rgb[1], rgb[2]] as [number, number, number] } : {}),
    ...(typeof params.surface?.roughness === 'number' ? { roughness: params.surface.roughness } : {}),
    ...(typeof params.surface?.metallic === 'number' ? { metallic: params.surface.metallic } : {}),
  };
}

// WHICH PASS OF A RUN THIS IS. A written build makes its model twice — a draft, then a
// correction of it — and both are kept, because whether the second was an improvement is a
// question two meshes answer and one does not.
//
// Null on everything else: a single-round run is not "pass 1 of 1", it is the run.
export const passOf = (job: Job3D): { at: number; of: number } | null => {
  const params = job.params as { pass?: number; passes?: number };
  return params.pass && params.passes ? { at: params.pass, of: params.passes } : null;
};

// The pass a pass corrected. NONE FOR A REBUILD, which is a version of a pass and not a link in
// the chain — including the rebuilds saved before versions, which were chained on as the run's
// next pass and are taken back out of it here.
const draftIdOf = (job: Job3D) =>
  rebuiltOf(job) ? undefined : (job.params as { draftJob?: number }).draftJob;

// A VERSION NOBODY WROTE: the same script with a dimension changed, built again. No call was
// made, so there is no question to show and nothing thought it. What it says instead is which
// mesh it came from, the pass it is a version of (`version`, absent on a rebuild made before
// versions existed) and which numbers moved. Null for a pass that was written.
export const rebuiltOf = (job: Job3D | null | undefined): { from: number; version?: number } | null =>
  ((job?.params as { rebuilt?: { from: number; version?: number } })?.rebuilt ?? null);

// THE ID OF THE PASS A MESH IS A VERSION OF — its own, for a pass. A rebuild that names no
// `version` is walked back along `from` to the first mesh that is not a rebuild.
export const versionRootId = (job: Job3D, jobs: Job3D[]): number => {
  const seen = new Set<number>();
  let at: Job3D = job;
  for (;;) {
    const rebuilt = rebuiltOf(at);
    if (!rebuilt || seen.has(at.id)) return at.id;
    if (rebuilt.version != null) return rebuilt.version;
    seen.add(at.id);
    const from = jobs.find((j) => j.id === rebuilt.from);
    if (!from) return rebuilt.from;
    at = from;
  }
};

// EVERY VERSION OF THE PASS THIS MESH BELONGS TO, oldest first. Version 1 is always the pass
// itself; the rest are its rebuilds, in the order they were made. A rebuild whose pass is gone
// is a group of its own.
export function versionGroup(job: Job3D, jobs: Job3D[]): Job3D[] {
  const root = versionRootId(job, jobs);
  const group = jobs.filter((j) => j.id === root || (rebuiltOf(j) && versionRootId(j, jobs) === root));
  return group.length ? [...group].sort((a, b) => a.id - b.id) : [job];
}

// The pass on screen, whichever of its versions is: the pass itself where it is still there.
const passOfVersion = (job: Job3D, jobs: Job3D[]): Job3D =>
  jobs.find((j) => j.id === versionRootId(job, jobs)) ?? job;

// EVERY PASS OF THE RUN THIS ONE BELONGS TO, oldest first — or just the run, where it
// made its model once.
//
// A CHAIN AND NOT A PAIR. Each pass records the one it was correcting (`draftJob`), so the
// group is walked forward to whichever pass nobody corrected and then back down the links.
// It was [draft, correction] exactly, which is every run made while two was the only
// number the route could do; three passes are two links, and the walk is the same code.
//
// Asked of either end, because either end can be the one on screen. The link is the id and
// not the order, so a deleted pass leaves the rest as a shorter run rather than half a pair
// — and `seen` is what stops a chain that somehow points at itself from being an infinite
// loop in a render.
//
// A VERSION ON SCREEN is asked as its pass: the passes are the same whichever version of one
// is shown, and no version is itself in the list.
export function passGroup(job: Job3D, jobs: Job3D[]): Job3D[] {
  const pass = passOfVersion(job, jobs);
  const seen = new Set<number>([pass.id]);
  let last = pass;
  for (;;) {
    const next = jobs.find((j) => draftIdOf(j) === last.id && !seen.has(j.id));
    if (!next) break;
    seen.add(next.id);
    last = next;
  }
  const chain = [last];
  for (;;) {
    const backId = draftIdOf(chain[0]);
    const back = backId == null ? undefined : jobs.find((j) => j.id === backId);
    if (!back || chain.some((j) => j.id === back.id)) break;
    chain.unshift(back);
  }
  return chain;
}

// HOW LONG THE WHOLE GENERATION TOOK: every call and every build, for every pass of it. The
// last pass carries that (see `took` in generate_written) and an earlier one only its own
// call, so it is read off the end of the group whichever pass is on screen. A run with no
// passes has its row's seconds.
export const generationTook = (job: Job3D, jobs: Job3D[]): number | null => {
  const group = passGroup(job, jobs);
  const last = group[group.length - 1];
  return (last.params as { took?: number }).took ?? last.elapsedSeconds;
};

// WHAT ONE PASS SPENT, as it recorded it: the call that wrote its script and the Blender that
// built it, in seconds. Null for a run that kept none — every written one made before they
// were kept.
export const stagesOf = (job: Job3D | null | undefined): { call?: number; build?: number } | null =>
  (job?.params as { stages?: { call?: number; build?: number } })?.stages ?? null;

// WHERE THE PASS ON SCREEN SPENT ITS SECONDS: the reasoning that wrote its script, and the
// Blender that built it. Two stages nothing alike — a call is minutes and a build is seconds
// — so a run that felt slow is answered by which of them it was.
//
// THIS PASS AND NOT THE RUN, which is where it parts company with `generationTook` above it.
// That total is the whole request whichever pass is shown, on purpose: it is the wait
// somebody sat through. But a breakdown that also totalled the run put the same two numbers
// under both halves of the pass toggle, which is the one control on the modal whose entire
// job is telling the passes apart — and what it is asked is what the SECOND look cost.
// `pass` says which one these are, so the two lines cannot be read as one.
//
// Null for a run that recorded none: every written one made before they were kept.
export const generationStages = (job: Job3D, jobs: Job3D[]): {
  call: number; build: number; pass: number; passes: number;
} | null => {
  const stages = stagesOf(job);
  if (!stages) return null;
  const group = passGroup(job, jobs);
  return {
    call: stages.call ?? 0,
    build: stages.build ?? 0,
    pass: group.findIndex((j) => j.id === versionRootId(job, jobs)) + 1,
    passes: group.length,
  };
};

// A run that is some other run's earlier pass. THE STRIP DRAWS ONE TILE PER RUN, not one
// per pass: two thumbnails a few pixels apart, of the same object at two stages, read as
// two attempts at the same part rather than as one attempt and its correction. The earlier
// passes are behind the toggle in the viewer, where comparing them is what you are there
// for.
//
// And a later VERSION of a pass is not a tile either: it is behind the version toggle, under the
// pass toggle. Unless its pass is gone, when it is all that is left of that pass.
export const isEarlierPass = (job: Job3D, jobs: Job3D[]): boolean =>
  jobs.some((j) => draftIdOf(j) === job.id)
  || (!!rebuiltOf(job) && passOfVersion(job, jobs).id !== job.id);

// THE PICTURES ONE CALL IS SHOWN, in the order it is shown them: the id, and the angle
// where the page knows one ('front', 'leftFront', … — see VIEW_DIRECTIONS; '' for a
// photograph nobody has classified).
//
// THE FIRST IS THE PRIMARY. It is the run's own `image_id` — so the row still says which
// photograph it is of and the strip still draws its thumbnail — it is what proportions are
// measured against, and it is the one the texture patch is cut from.
export type WrittenInput = { id: number; view?: string };

// WHERE A RUN IS, as the server last wrote it: which pass of how many, and which stage of it.
// 'thinking' covers writing the script too — OpenAI reports a call as in progress, not which.
// A Fix collisions run has its own three: 'fixing' (the call and its build), 'fitting' (the shallow
// ones cut away, and the build) and 'checking' (the new version's overlaps measured and drawn) —
// see _fix_loop.
export type RunStage = 'queued' | 'thinking' | 'building' | 'saving' | 'fixing' | 'fitting' | 'checking';
export type RunProgress = {
  pass: number; passes: number; stage: RunStage;
  // THE PASSES THAT ARE ALREADY ROWS, oldest first. A run saves each pass as it builds it, so
  // a page waiting on pass 4 has three meshes it could be looking at — and does, as soon as
  // this list grows. [] until the first one lands.
  saved: number[];
};

export async function generateWrittenMesh(
  source: PictureSource,
  // One or more, primary first: what one photograph cannot say, a second is for.
  inputs: WrittenInput[],
  model: string,
  opts: {
    // WHAT THE PRODUCT IS CALLED, for the one sentence the model is asked. Optional: a
    // photograph of a chair is a chair without it.
    subject?: string;
    // WHAT THIS RUN IS OF — how the panel finds its own runs again. See _product_mark on the server.
    product?: ProductMark;
    // HOW IT IS CALLED OFF: the browser's own name for this run, and the way to stop
    // waiting for it. Two halves of one press — see stopWritten.
    stop?: { token: string; signal: AbortSignal };
    // HOW MANY TIMES TO MODEL IT. Omitted is the server's default.
    passes?: number;
    // THE SIZE TO BUILD IT AT — see WrittenSize. The product's overall size or the part's own.
    size?: WrittenSize;
    // HOW HARD IT THINKS — one of the model's efforts. Omitted is the model's default.
    effort?: string;
    // TOLD WHERE THE RUN IS, at every poll that says.
    onProgress?: (progress: RunProgress) => void;
    // THE PARTS IT IS MADE OF — a whole product's; see WrittenPart.
    parts?: WrittenPart[];
    // AND WHAT THOSE PARTS ARE MADE OF, read once for the product by Phase 5's first
    // subphase. Sent rather than asked for: the build used to open by judging its own
    // materials off the same photographs, which made a colour a property of whichever run
    // was pressed. Empty is a run built on whatever glb.painted defaults to.
    materials?: Material[];
    // THE PARTS UP CLOSE, after the photographs — a product's selected listing crops, as the
    // photograph and its box. See productPartCrops.
    partCrops?: PartCrop[];
    // A PIECE OF HARDWARE'S PAGE CROPS, already cut — JPEG data URLs, since the page they are
    // cut from exists only in the browser. Sent after the drawings. See usePartStepCrops.
    stepCrops?: string[];
    // THE HARDWARE IT WORKS WITH, for a piece of hardware — see WrittenInteraction.
    interactions?: WrittenInteraction[];
    // A HARDWARE SET, built together — which manual, and the parts it holds with the catalog row
    // each piece is filed under when the run is kept. See _set_mark on the server.
    // `tool`: the catalog id of the part that drives the others, for a tool's set (see toolSet).
    set?: { docId: string; parts: PieceItem[]; tool?: number };
    // THE PARTS OF A SET NOT BUILT AGAIN, by name: their kept models, made with their tools, are put
    // in the scene and the rest is built around them. See _reused on the server.
    reuse?: { name: string }[];
    // AND WHAT EACH OF A SET'S PARTS IS MADE OF, painted on by the build after the script runs —
    // never told to the model. See build_code's `paint` on the server.
    paint?: { part: string; material: Material }[];
    // A TOOL'S SET: what its pieces are fastened to in their joints, driven by another tool — see
    // _partners on the server, which sends the ones built already.
    partners?: { piece: string; name: string; itemId: number }[];
  } = {},
): Promise<Job3D | null> {
  const {
    subject, product, stop, passes, size, effort, onProgress, parts, materials, partCrops,
    stepCrops, interactions, set, paint, reuse, partners,
  } = opts;
  // The run's name, which the server requires: it is how the page asks after the run.
  const token = stop?.token ?? newRunToken();
  const signal = stop?.signal;
  await axios.post(
    '/api/notice/3d/blender/generate',
    {
      source,
      // The primary under the name the row keeps it by, and the whole list beside it. Both,
      // because they answer different questions and the server reads them separately.
      imageId: inputs[0]?.id,
      imageIds: inputs.map((one) => one.id),
      imageViews: inputs.map((one) => one.view || ''),
      model,
      ...(subject ? { subject } : {}),
      ...(product ? { product } : {}),
      token,
      ...(passes ? { passes } : {}),
      ...(size ? { size } : {}),
      ...(parts?.length ? { parts } : {}),
      ...(materials?.length ? { materials } : {}),
      ...(partCrops?.length ? { partCrops } : {}),
      ...(stepCrops?.length ? { stepCrops } : {}),
      ...(interactions?.length ? { interactions } : {}),
      ...(set ? { set } : {}),
      ...(reuse?.length ? { reuse } : {}),
      ...(partners?.length ? { partners } : {}),
      ...(paint?.length ? { paint } : {}),
      ...(effort ? { effort } : {}),
    },
    signal ? { signal } : undefined,
  );
  return (await waitWrittenRun(token, signal, onProgress)).job;
}

// THE RUN IS OFF THE REQUEST. The POST answers as soon as the run has started, because a model at
// high effort thinks for longer than nginx keeps a request open; this asks after it until it ends,
// and the ending reads as the one request's did — the job, null where it was stopped, and a failure
// thrown as an axios error carrying the server's sentence. `note` is why a run that ended with work
// left stopped where it did (see _fix_loop).
async function waitWrittenRun(
  token: string,
  signal: AbortSignal | undefined,
  onProgress?: (progress: RunProgress) => void,
): Promise<{ job: Job3D | null; note?: string }> {
  let misses = 0;
  for (;;) {
    await pause(RUN_POLL_MS, signal);
    try {
      const { data } = await axios.get<
        { running?: boolean; job?: Job3D; stopped?: boolean; note?: string } & Partial<RunProgress>
      >('/api/notice/3d/written/run', { params: { token }, signal });
      misses = 0;
      if (!data.running) return { job: data.job ?? null, note: data.note };
      if (data.stage && data.pass && data.passes) {
        onProgress?.({
          pass: data.pass, passes: data.passes, stage: data.stage, saved: data.saved ?? [],
        });
      }
    } catch (e) {
      // A poll answered without a sentence — a dropped connection, nginx's 502 while a deploy
      // restarts gunicorn — is not the run failing: it is still being made, and a few are
      // forgiven. One WITH a sentence is the run's own ending.
      const said = axios.isAxiosError(e)
        && (e.response?.data as { error?: string } | undefined)?.error;
      if (axios.isCancel(e) || said || ++misses > RUN_POLL_RETRIES) throw e;
    }
  }
}

const RUN_POLL_MS = 3000;
const RUN_POLL_RETRIES = 10;

// A wait the Stop button can cut short, ending the way an aborted request does.
const pause = (ms: number, signal?: AbortSignal) => new Promise<void>((resolve, reject) => {
  const cut = () => {
    clearTimeout(timer);
    reject(new axios.CanceledError());
  };
  const timer = setTimeout(() => {
    signal?.removeEventListener('abort', cut);
    resolve();
  }, ms);
  if (signal?.aborted) cut();
  else signal?.addEventListener('abort', cut, { once: true });
});

// A NAME FOR A RUN THAT HAS NO ID YET, which is the whole reason it is the browser's to
// make: a run gets an id when it is saved, and stopping happens before that.
export const newRunToken = () =>
  (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);

// STOP: the call the run is waiting on is cancelled at OpenAI at its next poll and no pass
// after it is made. The passes that already landed are kept — they are saved as they are built,
// so they are on the strip being looked at when the button is pressed. What was spent before
// the press stays spent, and now it leaves something behind.
export async function stopWritten(token: string): Promise<void> {
  await axios.post('/api/notice/3d/written/stop', { token });
}

// THE SCRIPT A RUN WAS BUILT FROM, off its params — the only record of what the model
// actually wrote, and the first thing to read when a mesh comes back wrong. Empty for a run
// that was not written by anybody.
export const scriptOf = (job: Job3D): string =>
  typeof job.params?.script === 'string' ? job.params.script : '';
